import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { supabase } from '../supabaseClient'
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Chip,
  Fade,
  Alert,
  Button,
  Collapse,
  Container,
  Card,
  CardContent,
  Divider,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Tab,
  Tabs,
  alpha,
} from '@mui/material'
import { ListingCard } from '../ListingsCommon'
import { useTranslation } from 'react-i18next'
import ViewListIcon from '@mui/icons-material/ViewList'
import MapIcon from '@mui/icons-material/Map'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import SortIcon from '@mui/icons-material/Sort'
import TuneIcon from '@mui/icons-material/Tune'
import EuroIcon from '@mui/icons-material/Euro'
import CloseIcon from '@mui/icons-material/Close'
import HomeIcon from '@mui/icons-material/Home'
import HomeWorkIcon from '@mui/icons-material/HomeWork'
import ApartmentIcon from '@mui/icons-material/Apartment'
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap'
import LocationCityIcon from '@mui/icons-material/LocationCity'
import SearchIcon from '@mui/icons-material/Search'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import FilterListIcon from '@mui/icons-material/FilterList'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { motion } from 'framer-motion'
import StandaloneListingsMapView from './StandaloneListingsMapView'

export default function DiscoverPage() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))

  // State variables for listings and filters
  const [listings, setListings] = useState([])
  const [filteredListings, setFilteredListings] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [user, setUser] = useState(null)
  const [mapLoading, setMapLoading] = useState(false)

  // Infinite scroll for grid view
  const [page, setPage] = useState(0)
  const limit = 12
  const [hasMore, setHasMore] = useState(true)
  const sentinelRef = useRef(null)

  // View mode state toggle (grid vs. map)
  const [viewMode, setViewMode] = useState('grid')

  // Price filter state
  const [priceRange, setPriceRange] = useState([0, 0])
  const [maxPrice, setMaxPrice] = useState(0)

  // Property type filter
  const [propertyTypes, setPropertyTypes] = useState([])
  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState([])

  // Sort options
  const [sortOption, setSortOption] = useState('newest')
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null)

  // Search query
  const [searchQuery, setSearchQuery] = useState('')

  // Advanced filters dialog
  const [filtersOpen, setFiltersOpen] = useState(false)

  // Filter counts
  const [activeFilterCount, setActiveFilterCount] = useState(0)

  // Property feature filters
  const [numBedrooms, setNumBedrooms] = useState([0, 5])
  const [numBathrooms, setNumBathrooms] = useState([0, 5])
  const [surfaceArea, setSurfaceArea] = useState([0, 500])

  // Current category tab
  const [currentTab, setCurrentTab] = useState(0)

  // Track if component is mounted
  const isMounted = useRef(false)
  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  // 1. Fetch the authenticated user (once)
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data: userData } = await supabase.auth.getUser()
        if (userData && userData.user) {
          setUser(userData.user)
        }
      } catch (error) {
        console.error('Error fetching user:', error)
        setLoadError('Failed to fetch user data')
      }
    }
    fetchUser()
  }, [])

  // 2. Fetch listings for grid view with pagination
  useEffect(() => {
    const fetchListings = async () => {
      setLoading(true)
      setLoadError(null)

      try {
        const { data, error } = await supabase
          .from('listings')
          .select(
            '*, viewings(id, status, viewing_date, user_id), offers(id, offer_price, status, user_id)'
          )
          .is('sold_at', null)
          .order('created_at', { ascending: false })
          .range(page * limit, page * limit + limit - 1)

        if (error) {
          console.error('Error fetching listings:', error)
          setLoadError(`Error fetching listings: ${error.message}`)
          return
        }

        if (!isMounted.current) return

        if (data && data.length > 0) {
          if (page === 0) {
            const highestPrice = data.reduce((max, listing) => {
              return listing.price && listing.price > max ? listing.price : max
            }, 0)
            setMaxPrice(highestPrice)
            setPriceRange([0, highestPrice])

            // Extract property types
            const types = [...new Set(data.map((listing) => listing.property_type).filter(Boolean))]
            setPropertyTypes(types)
          }
          setListings((prev) => (page === 0 ? data : [...prev, ...data]))
          setHasMore(data.length === limit)
        } else {
          if (page === 0) {
            setListings([])
            setMaxPrice(0)
            setPriceRange([0, 0])
          }
          setHasMore(false)
        }
      } catch (error) {
        console.error('Error in fetchListings:', error)
        setLoadError(`Failed to load listings: ${error.message}`)
      } finally {
        if (isMounted.current) {
          setLoading(false)
        }
      }
    }
    fetchListings()
  }, [page])

  // 3. Infinite scrolling with Intersection Observer (grid view only)
  useEffect(() => {
    if (!sentinelRef.current) return
    if (!hasMore || loading) return

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading && hasMore && viewMode === 'grid') {
        setPage((prev) => prev + 1)
      }
    })

    observer.observe(sentinelRef.current)
    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current)
      }
    }
  }, [hasMore, loading, viewMode])

  // 4. Apply filters to listings
  useEffect(() => {
    if (listings.length > 0) {
      let filtered = listings.filter((listing) => {
        const price = listing.price || 0
        const inPriceRange = price >= priceRange[0] && price <= priceRange[1]

        // Property type filter
        const matchesPropertyType =
          selectedPropertyTypes.length === 0 ||
          (listing.property_type && selectedPropertyTypes.includes(listing.property_type))

        // Advanced filters
        const bedrooms = listing.bedrooms || 0
        const inBedroomRange = bedrooms >= numBedrooms[0] && bedrooms <= numBedrooms[1]

        const bathrooms = listing.bathrooms || 0
        const inBathroomRange = bathrooms >= numBathrooms[0] && bathrooms <= numBathrooms[1]

        const area = listing.surface || 0
        const inAreaRange = area >= surfaceArea[0] && area <= surfaceArea[1]

        // Search query
        const matchesSearch =
          !searchQuery ||
          (listing.name && listing.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (listing.description &&
            listing.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (listing.city && listing.city.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (listing.postcode && listing.postcode.toLowerCase().includes(searchQuery.toLowerCase()))

        return (
          inPriceRange &&
          matchesPropertyType &&
          inBedroomRange &&
          inBathroomRange &&
          inAreaRange &&
          matchesSearch
        )
      })

      // Apply sorting
      switch (sortOption) {
        case 'price_low':
          filtered.sort((a, b) => (a.price || 0) - (b.price || 0))
          break
        case 'price_high':
          filtered.sort((a, b) => (b.price || 0) - (a.price || 0))
          break
        case 'newest':
          filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          break
        case 'oldest':
          filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
          break
        case 'size':
          filtered.sort((a, b) => (b.surface || 0) - (a.surface || 0))
          break
        default:
          // Default to newest
          filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      }

      setFilteredListings(filtered)

      // Count active filters
      let count = 0
      if (priceRange[0] > 0 || priceRange[1] < maxPrice) count++
      if (selectedPropertyTypes.length > 0) count++
      if (numBedrooms[0] > 0 || numBedrooms[1] < 5) count++
      if (numBathrooms[0] > 0 || numBathrooms[1] < 5) count++
      if (surfaceArea[0] > 0 || surfaceArea[1] < 500) count++
      if (searchQuery) count++

      setActiveFilterCount(count)
    } else {
      setFilteredListings([])
    }
  }, [
    listings,
    priceRange,
    selectedPropertyTypes,
    numBedrooms,
    numBathrooms,
    surfaceArea,
    searchQuery,
    sortOption,
    maxPrice,
  ])

  // 5. Handle like/dislike changes
  const handleLikeDislikeChange = useCallback((listingId, newValue) => {
    setListings((prev) =>
      prev.map((listing) => (listing.id === listingId ? { ...listing, liked: newValue } : listing))
    )
  }, [])

  // Handle view mode change
  const handleViewModeChange = useCallback((event, newMode) => {
    if (newMode) {
      if (newMode === 'map') {
        setMapLoading(true)
        setTimeout(() => {
          setViewMode(newMode)
          setMapLoading(false)
        }, 100)
      } else {
        setViewMode(newMode)
      }
    }
  }, [])

  // Handle price range changes
  const handlePriceChange = (event, newValue) => {
    setPriceRange(newValue)
  }

  const handleMinPriceChange = (event) => {
    const value = Number(event.target.value.replace(/[^0-9]/g, ''))
    setPriceRange([value, priceRange[1]])
  }

  const handleMaxPriceChange = (event) => {
    const value = Number(event.target.value.replace(/[^0-9]/g, ''))
    setPriceRange([priceRange[0], value])
  }

  // Handle property type filter changes
  const handlePropertyTypeChange = (type) => {
    setSelectedPropertyTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    )
  }

  // Handle sort menu
  const handleSortClick = (event) => {
    setSortMenuAnchor(event.currentTarget)
  }

  const handleSortClose = () => {
    setSortMenuAnchor(null)
  }

  const handleSortChange = (option) => {
    setSortOption(option)
    handleSortClose()
  }

  // Handle search query changes
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value)
  }

  // Handle reset filters
  const handleResetFilters = () => {
    setPriceRange([0, maxPrice])
    setSelectedPropertyTypes([])
    setNumBedrooms([0, 5])
    setNumBathrooms([0, 5])
    setSurfaceArea([0, 500])
    setSearchQuery('')
    setSortOption('newest')
  }

  // Toggle advanced filters dialog
  const toggleFiltersDialog = () => {
    setFiltersOpen(!filtersOpen)
  }

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue)
  }

  // Format prices for display
  const formatPrice = (value) => {
    return `€${value.toLocaleString()}`
  }

  // Memoized grid view
  const gridView = useMemo(
    () => (
      <Fade in={viewMode === 'grid'} timeout={300}>
        <Box>
          {/* Empty state */}
          {filteredListings.length === 0 && !loading ? (
            <Box
              sx={{
                mt: 4,
                textAlign: 'center',
                p: 5,
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 1,
              }}
            >
              <SearchIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {t('discover.noListings', 'No listings available matching your criteria.')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Try adjusting your filters to see more results
              </Typography>
              <Button
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={handleResetFilters}
              >
                Reset Filters
              </Button>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredListings.map((listing) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  key={listing.id}
                  component={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ListingCard
                    listing={listing}
                    user={user}
                    onLikeDislikeChange={handleLikeDislikeChange}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Fade>
    ),
    [filteredListings, loading, t, user, handleLikeDislikeChange, viewMode, handleResetFilters]
  )

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Hero Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.05
          )} 0%, ${alpha(theme.palette.primary.light, 0.15)} 100%)`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            right: -50,
            top: -20,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(
              theme.palette.primary.main,
              0.1
            )}, transparent 70%)`,
            zIndex: 0,
          }}
        />

        <Grid container spacing={3} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid item xs={12} md={7}>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
              }}
            >
              {t('discover.title', 'Discover Your Perfect Home')}
            </Typography>
            <Typography variant="body1" paragraph color="text.secondary" sx={{ mb: 3 }}>
              {t(
                'discover.subtitle',
                'Browse through our curated listings and find the property that matches your needs and preferences perfectly.'
              )}
            </Typography>

            {/* Quick search */}
            <Box
              component="form"
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                maxWidth: 500,
                bgcolor: 'background.paper',
                borderRadius: 3,
                px: 2,
                py: 0.5,
                boxShadow: 2,
                mb: 2,
              }}
            >
              <SearchIcon color="action" sx={{ mr: 1 }} />
              <InputLabel htmlFor="search-field" sx={{ display: 'none' }}>
                Search
              </InputLabel>
              <OutlinedInput
                id="search-field"
                placeholder={t(
                  'discover.searchPlaceholder',
                  'Search by location, property name or features...'
                )}
                value={searchQuery}
                onChange={handleSearchChange}
                sx={{
                  flex: 1,
                  '& fieldset': { border: 'none' },
                  '& input': { py: 1.5 },
                }}
              />
              {searchQuery && (
                <IconButton edge="end" onClick={() => setSearchQuery('')} size="small">
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {/* Category tabs for quick filtering */}
            <Box sx={{ mb: 1 }}>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                aria-label="property categories"
                sx={{
                  '& .MuiTab-root': {
                    minWidth: 'auto',
                    px: 2,
                    py: 1,
                    mr: 1,
                    borderRadius: 2,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  },
                  '& .Mui-selected': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <Tab icon={<HomeIcon sx={{ mr: 1 }} />} iconPosition="start" label="All" />
                <Tab icon={<HomeWorkIcon sx={{ mr: 1 }} />} iconPosition="start" label="Houses" />
                <Tab
                  icon={<ApartmentIcon sx={{ mr: 1 }} />}
                  iconPosition="start"
                  label="Apartments"
                />
                <Tab
                  icon={<LocationCityIcon sx={{ mr: 1 }} />}
                  iconPosition="start"
                  label="New Developments"
                />
              </Tabs>
            </Box>
          </Grid>

          <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box
              component="img"
              src="/api/placeholder/450/250"
              alt="Discover properties"
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: 3,
                boxShadow: 3,
                transform: 'perspective(1000px) rotateY(-5deg)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'perspective(1000px) rotateY(0deg) translateY(-5px)',
                },
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {loadError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {loadError}
        </Alert>
      )}

      {/* Main Content with Filters and Listings */}
      <Grid container spacing={3}>
        {/* Left Sidebar - Filters */}
        <Grid item xs={12} md={3} lg={3} order={{ xs: 2, md: 1 }}>
          <Paper
            elevation={1}
            sx={{
              p: 2.5,
              borderRadius: 2,
              position: 'sticky',
              top: 20,
            }}
          >
            {/* Filter Header */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2.5 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FilterAltIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  {t('discover.filters', 'Filters')}
                </Typography>
              </Box>

              {activeFilterCount > 0 && (
                <Tooltip title={t('discover.resetFilters', 'Reset all filters')}>
                  <Button
                    size="small"
                    startIcon={<RestartAltIcon />}
                    onClick={handleResetFilters}
                    color="primary"
                    variant="text"
                  >
                    Reset
                  </Button>
                </Tooltip>
              )}
            </Stack>

            {/* Price Range Filter */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}
              >
                <EuroIcon fontSize="small" sx={{ mr: 0.5 }} />
                {t('discover.priceRange', 'Price Range')}
              </Typography>

              <Slider
                value={priceRange}
                onChange={handlePriceChange}
                valueLabelDisplay="auto"
                valueLabelFormat={formatPrice}
                min={0}
                max={maxPrice || 0}
                step={maxPrice > 5000 ? 10000 : 1000}
                sx={{ width: '100%', mt: 2, mb: 1 }}
              />

              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <FormControl variant="outlined" size="small" sx={{ flex: 1 }}>
                  <InputLabel htmlFor="min-price-input">{t('discover.min', 'Min')}</InputLabel>
                  <OutlinedInput
                    id="min-price-input"
                    value={priceRange[0].toLocaleString()}
                    onChange={handleMinPriceChange}
                    startAdornment={<InputAdornment position="start">€</InputAdornment>}
                    label={t('discover.min', 'Min')}
                  />
                </FormControl>
                <FormControl variant="outlined" size="small" sx={{ flex: 1 }}>
                  <InputLabel htmlFor="max-price-input">{t('discover.max', 'Max')}</InputLabel>
                  <OutlinedInput
                    id="max-price-input"
                    value={priceRange[1].toLocaleString()}
                    onChange={handleMaxPriceChange}
                    startAdornment={<InputAdornment position="start">€</InputAdornment>}
                    label={t('discover.max', 'Max')}
                  />
                </FormControl>
              </Stack>
            </Box>

            <Divider sx={{ my: 2.5 }} />

            {/* Property Type Filter */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}
              >
                <HomeWorkIcon fontSize="small" sx={{ mr: 0.5 }} />
                {t('discover.propertyType', 'Property Type')}
              </Typography>

              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
                {propertyTypes.map((type) => (
                  <Chip
                    key={type}
                    label={type}
                    clickable
                    color={selectedPropertyTypes.includes(type) ? 'primary' : 'default'}
                    variant={selectedPropertyTypes.includes(type) ? 'filled' : 'outlined'}
                    onClick={() => handlePropertyTypeChange(type)}
                    sx={{ mb: 1 }}
                  />
                ))}
              </Stack>
            </Box>

            <Divider sx={{ my: 2.5 }} />

            {/* More Filters Button */}
            <Button
              variant="outlined"
              fullWidth
              startIcon={<TuneIcon />}
              onClick={toggleFiltersDialog}
              sx={{ mb: 2 }}
            >
              {t('discover.moreFilters', 'More Filters')}
            </Button>

            {/* View listings button for mobile */}
            <Button
              variant="contained"
              fullWidth
              onClick={() =>
                window.scrollTo({
                  top: document.getElementById('listings-section').offsetTop - 20,
                  behavior: 'smooth',
                })
              }
              sx={{ display: { xs: 'block', md: 'none' } }}
            >
              {t('discover.viewListings', 'View Listings')}
              {filteredListings.length > 0 && ` (${filteredListings.length})`}
            </Button>
          </Paper>
        </Grid>

        {/* Main Content Area - Listings */}
        <Grid item xs={12} md={9} lg={9} order={{ xs: 1, md: 2 }}>
          <Box>
            {/* Control Bar */}
            <Paper
              sx={{
                p: 2,
                mb: 2.5,
                borderRadius: 2,
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
              elevation={1}
              id="listings-section"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {t('discover.results', 'Results')}
                </Typography>
                <Chip
                  label={filteredListings.length}
                  size="small"
                  color="primary"
                  variant="outlined"
                />

                {/* Active filters summary */}
                {activeFilterCount > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                    <Chip
                      icon={<FilterListIcon />}
                      label={`${activeFilterCount} ${t(
                        'discover.activeFilters',
                        'active filters'
                      )}`}
                      size="small"
                      color="default"
                      onDelete={handleResetFilters}
                      deleteIcon={<CloseIcon />}
                    />
                  </Box>
                )}
              </Box>

              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto', flexWrap: 'wrap' }}
              >
                {/* Show active filters on mobile */}
                {isMobile && activeFilterCount > 0 && (
                  <Badge badgeContent={activeFilterCount} color="primary">
                    <Button
                      size="small"
                      startIcon={<FilterAltIcon />}
                      onClick={toggleFiltersDialog}
                      variant="outlined"
                      sx={{ mr: 1 }}
                    >
                      {t('discover.filters', 'Filters')}
                    </Button>
                  </Badge>
                )}

                {/* Sort Menu */}
                <Button
                  size="small"
                  startIcon={<SortIcon />}
                  onClick={handleSortClick}
                  sx={{ mr: 1 }}
                  variant="outlined"
                >
                  {t('discover.sort', 'Sort')}
                </Button>
                <Menu
                  anchorEl={sortMenuAnchor}
                  open={Boolean(sortMenuAnchor)}
                  onClose={handleSortClose}
                >
                  <MenuItem
                    onClick={() => handleSortChange('newest')}
                    selected={sortOption === 'newest'}
                  >
                    {t('discover.sortNewest', 'Newest first')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleSortChange('price_low')}
                    selected={sortOption === 'price_low'}
                  >
                    {t('discover.sortPriceLow', 'Price: Low to high')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleSortChange('price_high')}
                    selected={sortOption === 'price_high'}
                  >
                    {t('discover.sortPriceHigh', 'Price: High to low')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleSortChange('size')}
                    selected={sortOption === 'size'}
                  >
                    {t('discover.sortSize', 'Largest size')}
                  </MenuItem>
                </Menu>

                {/* View Mode Toggle */}
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  aria-label="View mode"
                  size="small"
                  sx={{
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    borderRadius: '20px',
                    overflow: 'hidden',
                  }}
                >
                  <ToggleButton
                    value="grid"
                    aria-label="Grid view"
                    sx={{
                      borderRadius: '20px 0 0 20px',
                      px: 1.5,
                      border: 'none',
                    }}
                  >
                    <ViewListIcon fontSize="small" sx={{ mr: isMobile ? 0 : 0.5 }} />
                    {!isMobile && t('discover.gridView', 'Grid')}
                  </ToggleButton>
                  <ToggleButton
                    value="map"
                    aria-label="Map view"
                    sx={{
                      borderRadius: '0 20px 20px 0',
                      px: 1.5,
                      border: 'none',
                    }}
                  >
                    <MapIcon fontSize="small" sx={{ mr: isMobile ? 0 : 0.5 }} />
                    {!isMobile && t('discover.mapView', 'Map')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Paper>

            {/* Renders either grid view or map view based on selected mode */}
            {viewMode === 'grid' ? (
              gridView
            ) : (
              <Box
                sx={{
                  height: '600px',
                  position: 'relative',
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: 1,
                }}
              >
                {mapLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      bgcolor: alpha('#fff', 0.8),
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                <StandaloneListingsMapView priceRange={priceRange} />
              </Box>
            )}

            {/* Sentinel for infinite scroll */}
            {viewMode === 'grid' && (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                {loading && <CircularProgress size={30} />}
                <div ref={sentinelRef} style={{ height: '1px' }} />
                {!hasMore && !loading && filteredListings.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {t('discover.noMoreListings', 'You have seen all available listings')}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Advanced Filters Dialog */}
      <Dialog
        open={filtersOpen}
        onClose={toggleFiltersDialog}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {t('discover.advancedFilters', 'Advanced Filters')}
            </Typography>
            <IconButton edge="end" onClick={toggleFiltersDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={3}>
            {/* Bedrooms slider */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('discover.bedrooms', 'Bedrooms')}
              </Typography>
              <Slider
                value={numBedrooms}
                onChange={(e, newValue) => setNumBedrooms(newValue)}
                valueLabelDisplay="auto"
                min={0}
                max={5}
                step={1}
                marks={[
                  { value: 0, label: '0+' },
                  { value: 1, label: '1+' },
                  { value: 2, label: '2+' },
                  { value: 3, label: '3+' },
                  { value: 4, label: '4+' },
                  { value: 5, label: '5+' },
                ]}
              />
            </Box>

            {/* Bathrooms slider */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('discover.bathrooms', 'Bathrooms')}
              </Typography>
              <Slider
                value={numBathrooms}
                onChange={(e, newValue) => setNumBathrooms(newValue)}
                valueLabelDisplay="auto"
                min={0}
                max={5}
                step={1}
                marks={[
                  { value: 0, label: '0+' },
                  { value: 1, label: '1+' },
                  { value: 2, label: '2+' },
                  { value: 3, label: '3+' },
                  { value: 4, label: '4+' },
                  { value: 5, label: '5+' },
                ]}
              />
            </Box>

            {/* Surface Area slider */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('discover.surfaceArea', 'Surface Area (m²)')}
              </Typography>
              <Slider
                value={surfaceArea}
                onChange={(e, newValue) => setSurfaceArea(newValue)}
                valueLabelDisplay="auto"
                min={0}
                max={500}
                step={10}
                valueLabelFormat={(value) => `${value} m²`}
                marks={[
                  { value: 0, label: '0' },
                  { value: 100, label: '100' },
                  { value: 200, label: '200' },
                  { value: 300, label: '300' },
                  { value: 400, label: '400' },
                  { value: 500, label: '500+' },
                ]}
              />
            </Box>

            {/* Property features */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('discover.features', 'Property Features')}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                <Chip label="Garden" onClick={() => {}} variant="outlined" />
                <Chip label="Balcony" onClick={() => {}} variant="outlined" />
                <Chip label="Parking" onClick={() => {}} variant="outlined" />
                <Chip label="Furnished" onClick={() => {}} variant="outlined" />
                <Chip label="Pet Friendly" onClick={() => {}} variant="outlined" />
                <Chip label="Elevator" onClick={() => {}} variant="outlined" />
              </Stack>
            </Box>

            {/* Energy Label */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('discover.energyLabel', 'Energy Label')}
              </Typography>
              <Stack direction="row" spacing={1}>
                {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    variant="outlined"
                    onClick={() => {}}
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor:
                        label === 'A'
                          ? '#4CAF50'
                          : label === 'B'
                          ? '#8BC34A'
                          : label === 'C'
                          ? '#CDDC39'
                          : label === 'D'
                          ? '#FFEB3B'
                          : label === 'E'
                          ? '#FFC107'
                          : label === 'F'
                          ? '#FF9800'
                          : '#F44336',
                      color: label === 'A' || label === 'B' || label === 'C' ? 'white' : 'black',
                      fontWeight: 'bold',
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleResetFilters} startIcon={<RestartAltIcon />}>
            {t('discover.resetAll', 'Reset All')}
          </Button>
          <Button variant="contained" onClick={toggleFiltersDialog} startIcon={<FilterAltIcon />}>
            {t('discover.applyFilters', 'Apply Filters')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Filter Fab Button - Mobile Only */}
      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 999,
          }}
        >
          <Badge badgeContent={activeFilterCount} color="error">
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={toggleFiltersDialog}
              startIcon={<FilterAltIcon />}
              sx={{
                borderRadius: 8,
                px: 2,
                boxShadow: 3,
              }}
            >
              {t('discover.filters', 'Filters')}
            </Button>
          </Badge>
        </Box>
      )}
    </Container>
  )
}
